"use client";

import type { LucideIcon } from "lucide-react";
import Link from "next/link";
import type { JSX, ReactNode } from "react";

export interface TabItem<T extends string = string> {
  readonly value: T;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly href: string;
  readonly badge?: ReactNode;
}

export interface TabNavigationProps<T extends string = string> {
  readonly tabs: readonly TabItem<T>[];
  readonly activeTab: T;
  readonly onTabClick?: (tabValue: T) => void;
}

export function TabNavigation<T extends string>({
  tabs,
  activeTab,
  onTabClick,
}: TabNavigationProps<T>): JSX.Element {
  return (
    <div className="border-b border-border">
      <nav
        role="tablist"
        aria-orientation="horizontal"
        className="flex items-center gap-1 overflow-x-auto"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.value;

          return (
            <Link
              key={tab.value}
              href={tab.href}
              role="tab"
              aria-selected={isActive}
              aria-label={tab.label}
              data-state={isActive ? "active" : "inactive"}
              className={`px-4 py-3 text-[10px] font-medium sm:text-sm transition-colors relative flex items-center gap-2 ${
                isActive
                  ? "text-foreground font-semibold"
                  : "text-muted-foreground hover:text-foreground/90"
              }`}
              onClick={() => onTabClick?.(tab.value)}
            >
              <Icon className="h-4 w-4 hidden sm:block" aria-hidden="true" />
              {tab.label}
              {tab.badge}
              {isActive && (
                <span className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-foreground rounded-full" />
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
